import { useEffect, useRef, useState } from "react";
import {
  Badge,
  Button,
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
  Separator,
} from "@reactive-resume/ui";
import {
  ArrowClockwiseIcon,
  CheckCircleIcon,
  ClockIcon,
  LockIcon,
  PaperPlaneRightIcon,
  UserCirclePlusIcon,
} from "@phosphor-icons/react";
import { cn } from "@reactive-resume/utils";
import { Link } from "react-router";

import { useUser } from "@/client/services/user";
import { useDialog } from "@/client/stores/dialog";
import {
  useAssignHelp,
  useHelpRequest,
  useInternalAdmins,
  useRespondHelp,
  useSetHelpStatus,
} from "@/client/services/help/help";
import type { HelpRequest } from "@/client/services/help/help";

/**
 * ────────────────────────────────────────────────────────────────────────────
 *  FIL D'UNE DEMANDE D'AIDE — console support admin
 * ─────────────────────────────────────────────────────────────────────────────
 *  Plusieurs admins traitent la MÊME file en simultané : l'interface doit donc
 *  rendre impossible le doublon de traitement et le conflit d'écriture.
 *   - conversation chronologique (client vs support), lisible d'un coup d'œil ;
 *   - notes internes : jamais envoyées au client, visuellement distinctes ;
 *   - assignation nominative (« je prends cette demande ») ;
 *   - statut + horodatage de la 1re réponse (engagement de délai) ;
 *   - contexte technique transmis par le client (page, offre, référence).
 *  Toute réponse publique part aussi par e-mail au client (forceEmail serveur).
 * ─────────────────────────────────────────────────────────────────────────────
 */

const CATEGORY_LABELS: Record<HelpRequest["category"], string> = {
  DOWNLOAD_EXPORT: "Téléchargement & Export",
  PAYMENT: "Paiement & Crédits",
  ACCOUNT_SECURITY: "Compte & Sécurité",
  AI_FEATURE: "Fonctions IA",
  OTHER: "Autre demande",
};

const STATUS_META: Record<
  HelpRequest["status"],
  { label: string; variant: "warning" | "info" | "success" | "secondary" }
> = {
  OPEN: { label: "Ouverte", variant: "warning" },
  IN_PROGRESS: { label: "En cours", variant: "info" },
  RESOLVED: { label: "Résolue", variant: "success" },
  CLOSED: { label: "Clôturée", variant: "secondary" },
};

const STATUS_ORDER: HelpRequest["status"][] = ["OPEN", "IN_PROGRESS", "RESOLVED", "CLOSED"];

/** Âge lisible, coloré selon l'engagement de délai (rouge > 24 h, ambre > 4 h). */
const ageOf = (value: string) => {
  const hours = (Date.now() - new Date(value).getTime()) / 3600000;
  if (hours >= 24) return { label: `${Math.floor(hours / 24)} j`, urgent: true, warn: false };
  if (hours >= 4) return { label: `${Math.floor(hours)} h`, urgent: false, warn: true };
  if (hours < 1)
    return { label: `${Math.max(1, Math.round(hours * 60))} min`, urgent: false, warn: false };
  return { label: `${Math.floor(hours)} h`, urgent: false, warn: false };
};

const formatDateTime = (value?: string | null) =>
  value
    ? new Date(value).toLocaleString("fr-FR", { dateStyle: "medium", timeStyle: "short" })
    : "—";

export const HelpDetailDialog = () => {
  const { isOpen, close, payload } = useDialog<{ id: string }>("help-detail");
  const requestId = payload?.item?.id ?? "";

  const { request } = useHelpRequest(requestId);
  const { admins } = useInternalAdmins();
  const { user } = useUser();

  const respond = useRespondHelp();
  const setStatus = useSetHelpStatus();
  const assign = useAssignHelp();

  const [body, setBody] = useState("");
  const [internal, setInternal] = useState(false);
  const threadRef = useRef<HTMLDivElement | null>(null);

  // Le fil se cale en bas à l'ouverture et à chaque nouveau message.
  useEffect(() => {
    if (threadRef.current) threadRef.current.scrollTop = threadRef.current.scrollHeight;
  }, [request?.messages?.length, requestId]);

  if (!request) return null;

  const messages = request.messages ?? [];
  const age = ageOf(request.createdAt);
  const isClosed = request.status === "CLOSED";
  const isMine = Boolean(user?.id) && request.assignedAdminId === user?.id;
  const assignedName = admins.find((admin) => admin.id === request.assignedAdminId)?.name;

  /** Envoi : réponse client (e-mail) ou note interne (strictement privée). */
  const onSend = async () => {
    const trimmed = body.trim();
    if (!trimmed) return;

    await respond.mutateAsync({ id: request.id, body: trimmed, internalNote: internal });
    setBody("");
    setInternal(false);

    // Une réponse publique engage le traitement : on sort de l'état « ouverte ».
    if (!internal && request.status === "OPEN") {
      await setStatus.mutateAsync({ id: request.id, status: "IN_PROGRESS" });
    }
  };

  /** « Je prends cette demande » : assignation nominative + passage en cours. */
  const onClaim = async () => {
    if (!user?.id) return;
    await assign.mutateAsync({ id: request.id, adminId: user.id });
    if (request.status === "OPEN") {
      await setStatus.mutateAsync({ id: request.id, status: "IN_PROGRESS" });
    }
  };

  return (
    <Dialog open={isOpen} onOpenChange={close}>
      <DialogContent className="max-w-5xl gap-5">
        <DialogHeader>
          <div className="flex flex-wrap items-start justify-between gap-3">
            <div className="min-w-0">
              <DialogTitle className="flex items-center gap-2 text-lg">
                <span className="font-mono text-sm text-admin-text-muted">{request.code}</span>
                <span className="truncate">{request.subject}</span>
              </DialogTitle>

              <DialogDescription className="mt-2 flex flex-wrap items-center gap-x-3 gap-y-1 text-xs">
                <span>{CATEGORY_LABELS[request.category]}</span>
                <span className="text-admin-text-muted">•</span>
                <span
                  className={cn(
                    "inline-flex items-center gap-1 font-medium",
                    age.urgent
                      ? "text-red-600"
                      : age.warn
                        ? "text-amber-600"
                        : "text-admin-text-muted",
                  )}
                >
                  <ClockIcon size={12} /> ouverte il y a {age.label}
                </span>
                <span className="text-admin-text-muted">•</span>
                <span>
                  {request.user?.name} — {request.user?.email}
                </span>
              </DialogDescription>
            </div>

            <div className="flex shrink-0 items-center gap-2">
              <Badge variant={STATUS_META[request.status].variant}>
                {STATUS_META[request.status].label}
              </Badge>

              {request.assignedAdminId ? (
                <Badge className="border-0 bg-admin-surface-hover text-admin-text-secondary">
                  {isMine ? "Assignée à moi" : `Assignée à ${assignedName ?? "un admin"}`}
                </Badge>
              ) : (
                <Button size="sm" variant="outline" onClick={onClaim} disabled={assign.isPending}>
                  <UserCirclePlusIcon size={14} className="mr-1.5" />
                  {assign.isPending ? "Assignation…" : "Je prends"}
                </Button>
              )}
            </div>
          </div>
        </DialogHeader>

        <div className="grid gap-6 lg:grid-cols-[1fr_280px]">
          {/* ─ Conversation ─────────────────────────────────────────────── */}
          <div className="flex min-w-0 flex-col gap-4">
            {request.context && Object.keys(request.context).length > 0 && (
              <div className="rounded-xl border border-admin-border bg-admin-background p-4">
                <p className="mb-2 text-xs font-semibold uppercase tracking-wider text-admin-text-muted">
                  Contexte transmis par le client
                </p>
                <dl className="grid grid-cols-1 gap-x-6 text-xs sm:grid-cols-2">
                  {Object.entries(request.context).map(([key, value]) => (
                    <div
                      key={key}
                      className="flex justify-between gap-3 border-b border-admin-border/60 py-1"
                    >
                      <dt className="text-admin-text-muted">{key}</dt>
                      <dd className="truncate font-medium text-admin-text-secondary">
                        {String(value)}
                      </dd>
                    </div>
                  ))}
                </dl>
              </div>
            )}

            <div
              ref={threadRef}
              className="flex max-h-[44vh] min-h-[180px] flex-col gap-3 overflow-y-auto pr-1"
            >
              {messages.length === 0 && (
                <p className="py-8 text-center text-sm text-admin-text-muted">
                  Aucun message pour l'instant.
                </p>
              )}

              {messages.map((message) => {
                const fromSupport = message.authorRole === "admin";

                return (
                  <div
                    key={message.id}
                    className={cn("flex", fromSupport ? "justify-end" : "justify-start")}
                  >
                    <div
                      className={cn(
                        "max-w-[85%] rounded-xl border px-4 py-3",
                        message.internalNote
                          ? "border-amber-500/40 bg-amber-500/5"
                          : fromSupport
                            ? "border-admin-border bg-admin-surface"
                            : "border-admin-primary/30 bg-admin-primary/5",
                      )}
                    >
                      <div className="mb-1 flex items-center gap-2 text-[11px] uppercase tracking-wider text-admin-text-muted">
                        {message.internalNote ? (
                          <>
                            <LockIcon size={11} className="text-amber-600" />
                            Note interne
                          </>
                        ) : fromSupport ? (
                          "Support"
                        ) : (
                          "Client"
                        )}
                        <span>·</span>
                        <span>{formatDateTime(message.createdAt)}</span>
                      </div>

                      <p className="whitespace-pre-wrap text-sm text-admin-text-secondary">
                        {message.body}
                      </p>
                    </div>
                  </div>
                );
              })}
            </div>
            {/* Composer : réponse publique (e-mail au client) ou note interne */}
            <div className="flex flex-col gap-2">
              <div className="flex flex-wrap items-center gap-2">
                <button
                  type="button"
                  onClick={() => setInternal(false)}
                  className={cn(
                    "rounded-full border px-3 py-1 text-xs font-medium transition-colors",
                    !internal
                      ? "border-admin-primary bg-admin-primary text-admin-primary-foreground"
                      : "border-admin-border bg-admin-surface text-admin-text-secondary hover:text-admin-text-primary",
                  )}
                >
                  Réponse au client
                </button>

                <button
                  type="button"
                  onClick={() => setInternal(true)}
                  className={cn(
                    "inline-flex items-center gap-1 rounded-full border px-3 py-1 text-xs font-medium transition-colors",
                    internal
                      ? "border-amber-500/50 bg-amber-500/10 text-amber-600"
                      : "border-admin-border bg-admin-surface text-admin-text-secondary hover:text-admin-text-primary",
                  )}
                >
                  <LockIcon size={11} /> Note interne
                </button>

                <span className="ml-auto text-[11px] text-admin-text-muted">
                  {internal
                    ? "Visible par les admins uniquement"
                    : "Part aussi par e-mail au client"}
                </span>
              </div>

              <textarea
                value={body}
                onChange={(event) => setBody(event.target.value)}
                onKeyDown={(event) => {
                  if ((event.metaKey || event.ctrlKey) && event.key === "Enter") void onSend();
                }}
                rows={4}
                disabled={isClosed}
                placeholder={
                  isClosed
                    ? "Demande clôturée — réouvrez-la pour répondre."
                    : "Votre réponse… (Ctrl/⌘ + Entrée pour envoyer)"
                }
                className="w-full rounded-xl border border-admin-border bg-admin-background p-3 text-sm text-admin-text-primary outline-none focus-visible:ring-2 focus-visible:ring-admin-primary disabled:opacity-60"
              />

              <div className="flex flex-wrap items-center justify-between gap-2">
                <span className="text-[11px] text-admin-text-muted">
                  {request.firstResponseAt
                    ? `Première réponse : ${formatDateTime(request.firstResponseAt)}`
                    : "Aucune réponse envoyée — le client attend."}
                </span>

                <Button
                  size="sm"
                  onClick={onSend}
                  disabled={isClosed || respond.isPending || body.trim().length === 0}
                >
                  <PaperPlaneRightIcon size={14} className="mr-1.5" />
                  {respond.isPending
                    ? "Envoi…"
                    : internal
                      ? "Enregistrer la note"
                      : "Envoyer la réponse"}
                </Button>
              </div>
            </div>
          </div>

          <aside className="flex flex-col gap-4">
            <div className="rounded-xl border border-admin-border bg-admin-surface p-4">
              <p className="mb-3 text-xs font-semibold uppercase tracking-wider text-admin-text-muted">
                Traitement
              </p>

              <label className="mb-1 block text-xs text-admin-text-muted">Statut</label>
              <Select
                value={request.status}
                onValueChange={(value) =>
                  setStatus.mutate({ id: request.id, status: value as HelpRequest["status"] })
                }
              >
                <SelectTrigger className="border-admin-border bg-admin-background text-admin-text-secondary">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {STATUS_ORDER.map((status) => (
                    <SelectItem key={status} value={status}>
                      {STATUS_META[status].label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>

              <label className="mb-1 mt-4 block text-xs text-admin-text-muted">Assignée à</label>
              <Select
                value={request.assignedAdminId ?? "none"}
                onValueChange={(value) =>
                  assign.mutate({ id: request.id, adminId: value === "none" ? null : value })
                }
              >
                <SelectTrigger className="border-admin-border bg-admin-background text-admin-text-secondary">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="none">Non assignée</SelectItem>
                  {user?.id && <SelectItem value={user.id}>Moi ({user.name})</SelectItem>}
                  {admins
                    .filter((admin) => admin.id !== user?.id)
                    .map((admin) => (
                      <SelectItem key={admin.id} value={admin.id}>
                        {admin.name}
                      </SelectItem>
                    ))}
                </SelectContent>
              </Select>

              {request.status !== "RESOLVED" && request.status !== "CLOSED" && (
                <Button
                  size="sm"
                  variant="outline"
                  className="mt-4 w-full border-admin-border bg-admin-background text-admin-text-secondary"
                  onClick={() => setStatus.mutate({ id: request.id, status: "RESOLVED" })}
                >
                  <CheckCircleIcon size={14} className="mr-1.5" /> Marquer résolue
                </Button>
              )}
            </div>

            <div className="rounded-xl border border-admin-border bg-admin-surface p-4">
              <p className="mb-3 text-xs font-semibold uppercase tracking-wider text-admin-text-muted">
                Suivi
              </p>

              <dl className="flex flex-col gap-2 text-xs">
                <div className="flex justify-between gap-3">
                  <dt className="text-admin-text-muted">Reçue</dt>
                  <dd className="font-medium text-admin-text-secondary">
                    {formatDateTime(request.createdAt)}
                  </dd>
                </div>
                <Separator className="bg-admin-border" />
                <div className="flex justify-between gap-3">
                  <dt className="text-admin-text-muted">1re réponse</dt>
                  <dd className="font-medium text-admin-text-secondary">
                    {formatDateTime(request.firstResponseAt)}
                  </dd>
                </div>
                <Separator className="bg-admin-border" />
                <div className="flex justify-between gap-3">
                  <dt className="text-admin-text-muted">Résolue le</dt>
                  <dd className="font-medium text-admin-text-secondary">
                    {formatDateTime(request.resolvedAt)}
                  </dd>
                </div>
              </dl>
            </div>

            <div className="rounded-xl border border-admin-border bg-admin-surface p-4">
              <p className="mb-3 text-xs font-semibold uppercase tracking-wider text-admin-text-muted">
                Client
              </p>
              <p className="text-sm font-medium text-admin-text-primary">{request.user?.name}</p>
              <p className="mb-3 text-xs text-admin-text-muted">{request.user?.email}</p>

              <div className="flex flex-wrap gap-2">
                <Button
                  size="sm"
                  variant="outline"
                  className="border-admin-border bg-admin-background"
                  asChild
                >
                  <Link
                    to={`/admin/dashboard/client?search=${encodeURIComponent(request.user?.email ?? "")}`}
                  >
                    Fiche client
                  </Link>
                </Button>

                {(request.status === "RESOLVED" || request.status === "CLOSED") && (
                  <Button
                    size="sm"
                    variant="outline"
                    className="border-admin-border bg-admin-background"
                    onClick={() => setStatus.mutate({ id: request.id, status: "IN_PROGRESS" })}
                  >
                    <ArrowClockwiseIcon size={14} className="mr-1.5" /> Réouvrir
                  </Button>
                )}
              </div>
            </div>
          </aside>
        </div>
      </DialogContent>
    </Dialog>
  );
};
